import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  QrCode, Camera, Search, Check, X, AlertCircle, Users, Clock,
  ArrowLeft, RefreshCw, User, Loader2, Volume2, VolumeX
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sound effects
const playSuccess = () => {
  try {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleWEoR5+3xpBjKCxQfKnCmWs7OGCIp66KZD0zUX+cmXlXOUVojJaAaE5BUHiLfmpTRFF3hHZkVUhWeYJvYlVMVHp/amNaVV58em1lXllhfXhwamJeYn97c29pZWeBfnR0cGpqgn50dnJtcIN9dXhzcXaCfXV4c3R4gXx2eHV2eYB7d3h2eHt/enh5d3l8fXl5eHl6e3t6eXl5ent7enp6enp7e3p6enp6e3t7e3p6ent7e3t7e3t7');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {}
};

const playError = () => {
  try {
    const audio = new Audio('data:audio/wav;base64,UklGRjIFAABXQVZFZm10IBAAAAABAAEAgD4AAIA+AAABAAgAZGF0YQoFAABAgICAf399fXx7e3p5eHd2dXRzc3JxcXBwb29vb29vb29vcHBxcnNzdHV2d3l6e3x9fn+Ag4WGiImLjI6PkJKTlJaXmJqbnJ2en6ChoqOkpaamp6ipqausr66wsLGytLS1trm6u72+wMHDxcfJy87Q0tTW2Nrc3+Hi5Obn6evt8PL1+Pv/');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {}
};

const CheckInScannerPage = () => {
  const { eventId } = useParams();
  
  // State
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [manualEntry, setManualEntry] = useState('');
  const [checkInResult, setCheckInResult] = useState(null);
  const [recentCheckIns, setRecentCheckIns] = useState([]);
  const [stats, setStats] = useState({ total: 0, checked_in: 0, check_in_rate: 0 });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [cameraError, setCameraError] = useState(null);
  const [processingCheckIn, setProcessingCheckIn] = useState(false);
  
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);

  // Fetch event and stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get event details
        const eventRes = await fetch(`${API}/universal-events/events/${eventId}`);
        if (eventRes.ok) {
          const eventData = await eventRes.json();
          setEvent(eventData);
        }
        
        // Get check-in stats
        const statsRes = await fetch(`${API}/universal-events/events/${eventId}/check-ins`);
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats({
            total: statsData.total_tickets,
            checked_in: statsData.checked_in,
            check_in_rate: statsData.check_in_rate
          });
          setRecentCheckIns(statsData.attendees?.filter(a => a.checked_in).slice(0, 10) || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Failed to load event data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Refresh stats every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [eventId]);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // Start camera with html5-qrcode
  const startCamera = async () => {
    try {
      setCameraError(null);
      
      // Initialize html5-qrcode scanner
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;
      
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };
      
      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          // On successful scan
          handleQRScan(decodedText);
        },
        (errorMessage) => {
          // QR code not detected - this is called frequently, ignore it
        }
      );
      
      setScanning(true);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Camera access denied. Please allow camera permissions or use manual entry.');
      toast.error('Failed to start camera');
    }
  };

  // Stop camera
  const stopCamera = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error('Error stopping camera:', err);
      }
    }
    setScanning(false);
  };

  // Handle QR scan result
  const handleQRScan = async (qrData) => {
    // Prevent multiple scans while processing
    if (processingCheckIn) return;
    
    // Stop camera and process
    await stopCamera();
    await processCheckIn(null, qrData);
  };

  // Process check-in
  const processCheckIn = async (ticketNumber, qrCode) => {
    if (processingCheckIn) return;
    
    setProcessingCheckIn(true);
    setCheckInResult(null);
    
    try {
      const response = await fetch(`${API}/universal-events/check-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_number: ticketNumber,
          qr_code: qrCode,
          check_in_location: 'Main Entrance'
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setCheckInResult({
          success: true,
          attendee_name: data.attendee_name,
          ticket_number: data.ticket_number,
          message: data.message
        });
        
        if (soundEnabled) playSuccess();
        toast.success(`Welcome, ${data.attendee_name}!`);
        
        // Update stats
        setStats(prev => ({
          ...prev,
          checked_in: prev.checked_in + 1,
          check_in_rate: Math.round(((prev.checked_in + 1) / prev.total) * 100 * 10) / 10
        }));
        
        // Add to recent check-ins
        setRecentCheckIns(prev => [{
          attendee_name: data.attendee_name,
          ticket_number: data.ticket_number,
          check_in_time: new Date().toISOString()
        }, ...prev.slice(0, 9)]);
        
      } else {
        setCheckInResult({
          success: false,
          error: data.detail || 'Check-in failed'
        });
        
        if (soundEnabled) playError();
        toast.error(data.detail || 'Check-in failed');
      }
    } catch (err) {
      console.error('Check-in error:', err);
      setCheckInResult({
        success: false,
        error: 'Network error. Please try again.'
      });
      if (soundEnabled) playError();
    } finally {
      setProcessingCheckIn(false);
    }
    
    setManualEntry('');
  };

  // Handle manual entry
  const handleManualCheckIn = () => {
    if (!manualEntry.trim()) return;
    processCheckIn(manualEntry.trim().toUpperCase(), null);
  };

  // Scan next - reset state and optionally restart camera
  const handleScanNext = () => {
    setCheckInResult(null);
    // Optionally auto-restart camera
    // startCamera();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="check-in-scanner">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/admin/events/${eventId}`} className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-white">Check-in Scanner</h1>
                <p className="text-sm text-gray-400">{event?.name}</p>
              </div>
            </div>
            
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 text-gray-400 hover:text-white"
              title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
              data-testid="sound-toggle-btn"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-white" data-testid="stats-checked-in">{stats.checked_in}</p>
              <p className="text-xs text-gray-400">Checked In</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="stats-remaining">{stats.total - stats.checked_in}</p>
              <p className="text-xs text-gray-400">Remaining</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-500" data-testid="stats-progress">{stats.check_in_rate}%</p>
              <p className="text-xs text-gray-400">Progress</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Scanner Area */}
          <div className="space-y-4">
            {/* Camera View */}
            <div className="bg-gray-800 rounded-xl overflow-hidden">
              <div className="relative aspect-video bg-black" ref={scannerContainerRef}>
                {scanning ? (
                  <>
                    <div id="qr-reader" className="w-full h-full"></div>
                    <div className="absolute bottom-4 left-0 right-0 text-center">
                      <p className="text-white text-sm bg-black/50 inline-block px-3 py-1 rounded">
                        Point camera at QR code
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                    <QrCode className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-sm">Camera not active</p>
                    {cameraError && (
                      <p className="text-xs text-red-400 mt-2 px-4 text-center">{cameraError}</p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="p-4">
                <Button
                  onClick={scanning ? stopCamera : startCamera}
                  className={`w-full ${scanning ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                  data-testid="camera-toggle-btn"
                  disabled={processingCheckIn}
                >
                  {scanning ? (
                    <>
                      <X className="w-4 h-4 mr-2" />
                      Stop Camera
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      Start Camera
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Manual Entry */}
            <div className="bg-gray-800 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Manual Entry
              </h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualEntry}
                  onChange={(e) => setManualEntry(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualCheckIn()}
                  placeholder="Enter ticket number (e.g., MZN-TKT-2026-123456-01)"
                  className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400"
                  data-testid="manual-ticket-input"
                  disabled={processingCheckIn}
                />
                <Button
                  onClick={handleManualCheckIn}
                  disabled={!manualEntry.trim() || processingCheckIn}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="manual-checkin-btn"
                >
                  {processingCheckIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Check-in Result */}
            <AnimatePresence>
              {checkInResult && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={`rounded-xl p-6 ${
                    checkInResult.success 
                      ? 'bg-green-900/50 border border-green-500' 
                      : 'bg-red-900/50 border border-red-500'
                  }`}
                  data-testid="check-in-result"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      checkInResult.success ? 'bg-green-500' : 'bg-red-500'
                    }`}>
                      {checkInResult.success ? (
                        <Check className="w-6 h-6 text-white" />
                      ) : (
                        <X className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <div>
                      {checkInResult.success ? (
                        <>
                          <h3 className="text-xl font-bold text-white">{checkInResult.message}</h3>
                          <p className="text-green-300">{checkInResult.ticket_number}</p>
                        </>
                      ) : (
                        <>
                          <h3 className="text-xl font-bold text-white">Check-in Failed</h3>
                          <p className="text-red-300">{checkInResult.error}</p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleScanNext}
                    variant="outline"
                    className="mt-4 w-full border-gray-600 text-white hover:bg-gray-700"
                    data-testid="scan-next-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Scan Next
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Recent Check-ins */}
          <div className="bg-gray-800 rounded-xl p-4">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Recent Check-ins
            </h3>
            
            {recentCheckIns.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No check-ins yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="recent-checkins-list">
                {recentCheckIns.map((checkin, idx) => (
                  <div
                    key={`${checkin.ticket_number}-${idx}`}
                    className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg"
                    data-testid={`checkin-item-${idx}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{checkin.attendee_name}</p>
                      <p className="text-xs text-gray-400">{checkin.ticket_number}</p>
                    </div>
                    <div className="text-xs text-gray-500">
                      {checkin.check_in_time && new Date(checkin.check_in_time).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSS for html5-qrcode scanner */}
      <style>{`
        #qr-reader {
          width: 100%;
          height: 100%;
        }
        #qr-reader video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover;
        }
        #qr-reader__scan_region {
          min-height: 100% !important;
        }
        #qr-reader__header_message {
          display: none !important;
        }
        #qr-reader__dashboard {
          display: none !important;
        }
        #qr-reader__dashboard_section {
          display: none !important;
        }
        #qr-reader__dashboard_section_csr {
          display: none !important;
        }
        #qr-reader__dashboard_section_swaplink {
          display: none !important;
        }
      `}</style>
    </div>
  );
};

export default CheckInScannerPage;
